import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionsGroup } from 'src/app/shared/models/SessionsGroup';
import { AccessService } from 'src/app/shared/services/access.service';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Utils } from 'src/app/shared/utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-access-question',
  templateUrl: './access-question.component.html',
  styleUrls: ['./access-question.component.css']
})
export class AccessQuestionComponent {
  busy: Subscription;
  groupSessionId: number;
  congressId: number;
  sessionGroup: SessionsGroup;
  filteredQuestions: any[] = [];
  itemsPerPage: number = 5;
  searchTerm: string = '';
  currentPages: { [tab: string]: number } = { 'allQuestion': 1, 'readed': 1, 'unreaded': 1 };
  itemsPerPageOptions = [5, 10, 20, 50, -1];
  activeParticipantCount: number = 0;
  isLoading: boolean = true;
  selectedAccessNames: string[] = [];
  totalQuestionsCount: number = 0;
  isTableVisible: boolean = false;
  roomName: string;
  roomListeners: any[] = [];
  filesUrl = environment.filesUrl;
  constructor(private route: ActivatedRoute,
    private userService: UserService,
    private accessService: AccessService,
    private db: AngularFireDatabase,
    private translate: TranslateService) {
    this.congressId = parseInt(this.route.snapshot.paramMap.get('congress_id'));
    this.groupSessionId = parseInt(this.route.snapshot.paramMap.get('session_group_id'));
  }

  ngOnInit() {
    this.getSessionsGroupById();
  }

  ngOnDestroy() {
    this.roomListeners.forEach(unsub => unsub());
  }

  getSessionsGroupById() {
    this.busy = this.accessService.getSessionsGroupById(this.congressId, this.groupSessionId).subscribe(
      (data: any) => {
        this.sessionGroup = data;
        this.loadFilteredQuestions();
      },
      (error) => {
      }
    );
  }

  updatedVisibilityChange(question: any, isChecked: boolean) {
    if (!question || !question.access_id || !question.timeStamp) {
      console.error('Question is undefined or missing necessary fields');
      return;
    }

    const roomName = Utils.getRoomNames(String(this.congressId), question.access_id);
    const messageKey = question && question.firebaseKey ? question.firebaseKey : null;
    const path = `chat/${roomName}/messages/${messageKey}`;
    const updatedVisibility = isChecked ? 1 : 0;
    this.db.database.ref(path).update({
      visible: updatedVisibility
    }).catch(error => {
      console.error('Erreur lors de la mise à jour de la visibilité :', error);
    });
  }

  async loadFilteredQuestions() {
    this.isLoading = true;
    const user = await this.userService.userAuth(environment.firebase.mail, environment.firebase.password);
    if (!user) return;
    // Reset des anciens listeners
    this.roomListeners.forEach(unsub => unsub());
    this.roomListeners = [];
    this.filteredQuestions = [];

    const uniqueUserIds = new Set<string>();

    if (!this.sessionGroup || !this.sessionGroup.accesses) {
      console.warn('Pas de accesses dans sessionGroup');
      this.isLoading = false;
      return;
    }
    const accesses = this.sessionGroup.accesses.filter(access => access != null && access.access_id != null);
    accesses.forEach((access: any) => {
      const roomName = Utils.getRoomNames(String(this.congressId), access.access_id);
      const path = `chat/${roomName}/messages`;
      const ref = this.db.database.ref(path);

      const listener = ref.on('value', (snapshot) => {
        const currentMessages: any[] = [];

        if (snapshot.exists()) {
          snapshot.forEach((childSnapshot) => {
            const message = childSnapshot.val();
            const firebaseKey = childSnapshot.key;

            if (message.user_id) {
              uniqueUserIds.add(message.user_id);
            }

            currentMessages.push({
              ...message,
              firebaseKey,
              timeStamp: message.timeStamp,
            });
          });
        }

        this.filteredQuestions = [
          ...this.filteredQuestions.filter(m => m.roomName !== roomName),
          ...currentMessages.map(m => ({ ...m, roomName }))
        ].sort((a, b) => this.getLatestTimestamp(b) - this.getLatestTimestamp(a));

        this.totalQuestionsCount = this.getUnfilteredQuestions().length;
        this.activeParticipantCount = uniqueUserIds.size;
        this.isLoading = false;
      });

      this.roomListeners.push(() => ref.off('value', listener));
    });
  }
  
  getLatestTimestamp(question: any): number {
    let latestTimestamp = 0;
    latestTimestamp = Math.max(latestTimestamp, parseInt(question.timeStamp));
    return latestTimestamp;
  }

  getFormattedFilteredQuestions(tab: string): any[] {
    let result = this.getUnfilteredQuestions();
    if (this.searchTerm) {
      const term = this.searchTerm.trim().toLowerCase();
      result = result.filter(item => {
        const isAnonymeTerm = 'anonyme'.includes(term);
        const anonymeMatch = item.anonyme && isAnonymeTerm;
        const nameMatch = !item.anonyme && item.name?.toLowerCase().includes(term);
        const otherMatch = item.question?.toLowerCase().includes(term) ||
          item.access_name?.toLowerCase().includes(term);
        return anonymeMatch || nameMatch || otherMatch;
      });
    }
    if (this.selectedAccessNames.length > 0) {
      result = result.filter(item => this.selectedAccessNames.includes(item.access_name));
    }
    return result;
}

  trackByFn(item: any): string {
    return item.id;
  }

  get questionReaded(): any[] {
    return this.getFormattedFilteredQuestions('allQuestion').filter(q => q.visible === 1);
  }

  get questionUnreaded(): any[] {
    return this.getFormattedFilteredQuestions('allQuestion').filter(q => q.visible === 0);
  }

  truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  currentPage(tab: string): number {
    return this.currentPages[tab];
  }

  goToPage(tab: string, page: number, event: Event): void {
    event.preventDefault();
    if (page >= 1 && page <= this.totalPages(tab)) {
      this.currentPages[tab] = page;
    }
  }

  getPaginatedQuestions(tab: string): any[] {
    const questions = this.getFormattedFilteredQuestions(tab);
    if (this.itemsPerPage == -1) {
      return questions;
    }
    const startIndex = Math.max(0, (this.currentPages[tab] - 1) * this.itemsPerPage);
    const endIndex = Math.min(startIndex + this.itemsPerPage, questions.length);
    const paginatedQuestions = questions.slice(startIndex, endIndex);
    return paginatedQuestions;
  }

  totalPages(tab: string): number {
    let totalItems = 0;

    if (tab === 'readed') {
      totalItems = this.questionReaded?.length || 0;
    } else if (tab === 'unreaded') {
      totalItems = this.questionUnreaded?.length || 0;
    } else {
      totalItems = this.getFormattedFilteredQuestions(tab)?.length || 0;
    }
    // Cas spécial pour "All" (-1)
    if (this.itemsPerPage === -1) {
      return 1;
    }

    if (this.itemsPerPage <= 0 || totalItems <= 0) {
      return 1;
    }

    return Math.max(1, Math.ceil(totalItems / this.itemsPerPage));
  }

  private getPaginated(questions: any[], pageKey: string): any[] {
    if (this.itemsPerPage === -1) {
      return questions;
    }
    const currentPage = this.currentPages[pageKey] || 1;
    const startIndex = Math.max(0, (currentPage - 1) * this.itemsPerPage);
    const endIndex = Math.min(startIndex + this.itemsPerPage, questions.length);
    return questions.slice(startIndex, endIndex);
  }

  getPaginatedVisible(): any[] {
    if (this.itemsPerPage == -1) {
      return this.questionReaded || [];
    }
    return this.getPaginated(this.questionReaded, 'readed');
  }

  getPaginatedNotVisible(): any[] {
    if (this.itemsPerPage == -1) {
      return this.questionUnreaded || [];
    }
    return this.getPaginated(this.questionUnreaded, 'unreaded');
  }

  getProgressPercentage(): number {
    const total = this.getFormattedFilteredQuestions('allQuestion').length;
    const visible = this.getCurrentPageQuestions().length;
    return total > 0 ? (visible / total) * 100 : 0;
  }

  toggleAccessNameSelection(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const value = checkbox.value;

    if (checkbox.checked) {
      if (!this.selectedAccessNames.includes(value)) {
        this.selectedAccessNames.push(value);
      }
    } else {
      this.selectedAccessNames = this.selectedAccessNames.filter(name => name !== value);
    }
  }

  getAllAccessNames(): string[] {
    const allNames = new Set<string>();
    for (const item of this.sessionGroup.accesses) {
      if (item.name) {
        allNames.add(item.name);
      }
    }

    return Array.from(allNames);
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    Object.keys(this.currentPages).forEach(tab => {
      this.currentPages[tab] = 1;
    });
  }

  displayDate(dateString: string): string {
    return Utils.formatDateTexteString(dateString, this.translate);
  }

  toggleTable() {
    this.isTableVisible = !this.isTableVisible;
  }

  getCurrentPageQuestions(): any[] {
    const allQuestions = this.getFormattedFilteredQuestions('allQuestion');
    const visibleQuestions = allQuestions.filter(question => question.visible === 1);
    return visibleQuestions;
  }

  getUnfilteredQuestions(): any[] {
    const rows: any[] = [];
    const seenQuestions = new Set<string>();
    
    for (const item of this.filteredQuestions) {
      const uniqueKey = `${item.user_id}_${item.messageBody}_${item.timeStamp}`;
      if (!seenQuestions.has(uniqueKey)) {
        seenQuestions.add(uniqueKey);
  
        const date = new Date(+item.timeStamp);
        const formattedTimestamp = date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).replace(',', '');
  
        const access = this.sessionGroup?.accesses?.find(acc => acc.access_id === item.access_id);

        const speakerNames = access?.speakers?.length
          ? access.speakers.map(speaker => speaker.first_name + ' ' + speaker.last_name)
          : ['Aucun conférencier'];
        
        rows.push({
          id: uniqueKey,
          name: item.senderName,
          question: item.messageBody,
          access_name: item.access_name,
          anonyme: item.anonyme,
          timeStamp: item.timeStamp,
          formattedTimestamp,
          visible: item.visible ?? 0,
          access_id: item.access_id,
          user_id: item.user_id,
          session_group_id: item.session_group_id,
          firebaseKey: item.firebaseKey,
          speakers: speakerNames
        });
      }
    }
  
    return rows.sort((a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp));
  }
}
